import { OpsType } from '@weave/schema-gen-ts/dist/shared/organization/ops_type.pb';
import { MutationOptions, UseQueryOptions, UseQueryResult, useQueries, useQueryClient } from 'react-query';
import { useMutation, useQuery } from '@frontend/react-query-helpers';
import { SchemaPaymentsCommandsService } from '@frontend/schema';
import { getPaymentNotificationSettings, updatePaymentNotificationSettings } from './api';
import { PaymentsClient } from './payments-client';
import {
  PaymentNotificationSettingsResponse,
  PaymentsUnrecordedCountRequest,
  PaymentsUnrecordedCountResponse,
} from './types';

const HOURS_IN_MS = 1000 * 60 * 60;

export const paymentsQueryKeys = {
  baseQuery: ['payments'],
  unreadCountBase: () => [...paymentsQueryKeys.baseQuery, 'unread-count'],
  getNotificationSettings: (locationId: string) => [
    ...paymentsQueryKeys.baseQuery,
    'notification-settings',
    locationId,
  ],
  getUnreadCount: (locationIds: string[]) => [...paymentsQueryKeys.unreadCountBase(), ...locationIds],
};

export const usePaymentsInvalidation = () => {
  const queryClient = useQueryClient();

  const invalidateNotificationSettings = (locationId: string) => {
    queryClient.invalidateQueries(paymentsQueryKeys.getNotificationSettings(locationId));
  };

  const invalidateUnrecordedCount = (locationIds?: string[]) => {
    queryClient.invalidateQueries({
      predicate: ({ queryKey }) =>
        paymentsQueryKeys.unreadCountBase().every((part) => queryKey.includes(part)) &&
        (!locationIds?.length || locationIds?.some((locationId) => queryKey.includes(locationId))),
    });
  };

  return {
    invalidateUnrecordedCount,
    invalidateNotificationSettings,
  };
};

export const usePaymentNotificationSettingsQuery = (
  locationIds: string[],
  options?: UseQueryOptions<PaymentNotificationSettingsResponse>
): UseQueryResult<PaymentNotificationSettingsResponse>[] => {
  return useQueries(
    locationIds.map((locationId) => ({
      queryKey: paymentsQueryKeys.getNotificationSettings(locationId),
      queryFn: () => getPaymentNotificationSettings(locationId),
      cacheTime: 12 * HOURS_IN_MS,
      staleTime: 12 * HOURS_IN_MS,
      ...options,
      enabled: (options?.enabled ?? true) && !!locationId,
    }))
  );
};

export const usePaymentNotificationSetttingsMutate = (
  options?: MutationOptions<unknown, unknown, { payload: PaymentNotificationSettingsResponse[] }>
) => {
  return useMutation({
    mutationFn: (requestData) => {
      return Promise.all(requestData.payload.map((data) => updatePaymentNotificationSettings(data)));
    },
    ...options,
  });
};

export const usePaymentsUnrecordedCount = (
  request: PaymentsUnrecordedCountRequest,
  options?: UseQueryOptions<PaymentsUnrecordedCountResponse, unknown, number | undefined>
) => {
  return useQuery({
    queryKey: paymentsQueryKeys.getUnreadCount(request.locationIds),
    queryFn: () => SchemaPaymentsCommandsService.GetUnrecordedPaymentsCount(request),
    cacheTime: 12 * HOURS_IN_MS,
    staleTime: 12 * HOURS_IN_MS,
    ...options,
    enabled: (options?.enabled ?? true) && !!request?.locationIds?.length,
    select: (data) => data?.count,
  });
};

export const useStripeExpressPublicKeyQuery = (locationType?: keyof typeof OpsType) => {
  return useQuery({
    queryKey: [locationType, PaymentsClient.queryKeys.getStripeExpressPublicKey],
    queryFn: () => PaymentsClient.getStripeExpressPublicKey(locationType as keyof typeof OpsType),
    enabled: !!locationType,
  });
};
